/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Google PubSub", description = "Hightouch and Google PubSub integration", lastUpdatedDate = new Date(1722211200000), icon = "https://simpleicons.org/icons/googlepubsub.svg";
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Google PubSub",
        description: "Hightouch and Google PubSub integration",
        lastUpdatedDate: "Mon Jul 29 2024 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "google-pubsub",
        type: "source",
        icon: "https://simpleicons.org/icons/googlepubsub.svg",
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Setup",
          "depth": 0,
          "id": "setup"
        }, {
          "value": "Configure your source",
          "depth": 1,
          "id": "configure-your-source"
        }, {
          "value": "Grant permissions",
          "depth": 1,
          "id": "grant-permissions"
        }, {
          "value": "Event Structure",
          "depth": 0,
          "id": "event-structure"
        }, {
          "value": "Field mappings",
          "depth": 1,
          "id": "field-mappings"
        }, {
          "value": "Schema Enforcement",
          "depth": 1,
          "id": "schema-enforcement"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      a: "a",
      strong: "strong",
      h3: "h3",
      ul: "ul",
      li: "li",
      code: "code",
      pre: "pre",
      span: "span"
    }, _provideComponents(), props.components), {CommonErrorsPreface} = _components;
    if (!CommonErrorsPreface) _missingMdxReference("CommonErrorsPreface", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch supports streaming events from Google PubSub in realtime."
      }), "\n", _jsx(_components.p, {
        children: "Simply configure your Google PubSub source in the Hightouch UI, and Hightouch will automatically extract, transform, and load your data into Hightouch Events. Your data can then be synced to your warehouse or streamed to our catalog of realtime destinations."
      }), "\n", _jsx(_components.h2, {
        id: "setup",
        children: "Setup"
      }), "\n", _jsxs(_components.p, {
        children: ["To get started, ", _jsx(_components.a, {
          href: "https://app.hightouch.com/events/sources/new",
          children: _jsx(_components.strong, {
            children: "create an event source"
          })
        }), ", select ", _jsx(_components.strong, {
          children: "Google PubSub"
        }), " and follow the steps below."]
      }), "\n", _jsx(_components.h3, {
        id: "configure-your-source",
        children: "Configure your source"
      }), "\n", _jsx(_components.p, {
        children: "Configure the following fields in Hightouch:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Credentials"
          }), ": GCP credentials for connecting to PubSub."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Subscription"
          }), ": The full name of the PubSub subscription to consume events from."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Project ID"
          }), ": The Google Cloud Project ID."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Regional API Endpoint"
          }), ": ", _jsx(_components.a, {
            href: "https://cloud.google.com/pubsub/docs/reference/service_apis_overview#pubsub_endpoints",
            children: "List of endpoints"
          })]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "grant-permissions",
        children: "Grant permissions"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch believes in the principle of least privilege. We ask for no more permissions than necessary. For the Google PubSub event source, Hightouch requires ", _jsx(_components.code, {
          children: "roles/pubsub.subscriber"
        }), " and ", _jsx(_components.code, {
          children: "roles/pubsub.viewer"
        }), ". Ensure you run the command in your Google Cloud Console to grant Hightouch's service account the correct permissions."]
      }), "\n", _jsx(_components.h2, {
        id: "event-structure",
        children: "Event Structure"
      }), "\n", _jsxs(_components.p, {
        children: ["All events are ingested into Hightouch as ", _jsx(_components.code, {
          children: "track"
        }), " events. The PubSub message ", _jsx(_components.code, {
          children: "id"
        }), ", ", _jsx(_components.code, {
          children: "data"
        }), ", and ", _jsx(_components.code, {
          children: "attributes"
        }), " are exposed as custom ", _jsx(_components.code, {
          children: "properties"
        }), " on the ", _jsx(_components.code, {
          children: "track"
        }), " event. Metadata such as ", _jsx(_components.code, {
          children: "subscription"
        }), " are also exposed in the ", _jsx(_components.code, {
          children: "context.pubsub"
        }), " object."]
      }), "\n", _jsx(_components.p, {
        children: "For example, the following PubSub message:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-json",
          children: [_jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"id\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"123\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"data\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"user_id\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"user_123\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"amount\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-number",
            children: "123.45"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"attributes\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"a1\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"abc\""
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n"]
        })
      }), "\n", _jsx(_components.p, {
        children: "Will be ingested into Hightouch as:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-json",
          children: [_jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"type\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"track\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"event\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Google PubSub Event\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"properties\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"id\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"123\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"data\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"user_id\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"user_123\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"amount\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-number",
            children: "123.45"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"attributes\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"a1\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"abc\""
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"context\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"pubsub\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"subscription\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"projects/my-project/subscriptions/my-subscription\""
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"timestamp\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"2024-07-29T14:52:37.648Z\""
          }), "\n", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n"]
        })
      }), "\n", _jsx(_components.h3, {
        id: "field-mappings",
        children: "Field mappings"
      }), "\n", _jsxs(_components.p, {
        children: ["You can configure how Hightouch transforms messages from your PubSub topic to Hightouch ", _jsxs(_components.a, {
          href: "/events/event-spec#track-events",
          children: [_jsx(_components.code, {
            children: "track"
          }), " events"]
        }), ". Mappings can be configured for the following fields:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "event"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "userId"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "anonymousId"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "messageId"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "timestamp"
          })
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "You can also transform your events using Functions in order to standardize the schema or enrich its contents."
      }), "\n", _jsx(_components.h3, {
        id: "schema-enforcement",
        children: "Schema Enforcement"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.a, {
          href: "/events/contracts/management",
          children: "Event Contracts"
        }), " can be attached to Google PubSub sources just like any other Event Source."]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(CommonErrorsPreface, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
